import { Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { AnalyticsOverviewEmpty } from "@/components/brand/analytics/analytics-empty-state"
import { AnalyticsErrorState } from "@/components/brand/analytics/analytics-error-state"
import { AnalyticsFilterBar } from "@/components/brand/analytics/analytics-filter-bar"
import { AnalyticsOverviewKpiStrip } from "@/components/brand/analytics/analytics-overview-kpi-strip"
import { AnalyticsTopMissionsTable } from "@/components/brand/analytics/analytics-top-missions-table"
import { AnalyticsTrendChart } from "@/components/brand/analytics/analytics-trend-chart"
import { BrandAnalyticsTabs } from "@/components/brand/analytics/brand-analytics-tabs"
import AppLayout from "@/layouts/app-layout"
import type { MissionAnalyticsOverviewProps } from "@/types/mission-analytics"

/**
 * OKS-63 — Cross-mission analytics overview (`/brand/analytics/missions`).
 *
 * # State branch (TRD §4)
 *
 *   1. `state === "error"` → `<AnalyticsErrorState>` with Retry.
 *   2. `state === "cold_start"` → `<AnalyticsOverviewEmpty>` (CTA to
 *      create the first mission).
 *   3. `state === "empty_in_window"` → filter bar + KPI strip (zeros)
 *      + chart empty card + table empty card.
 *   4. `state === "success"` → filter bar + KPI strip + trend chart +
 *      top-5 table.
 *
 * # Partial reload
 *
 * Filter changes are routed via `<AnalyticsFilterBar>`'s `router.get`
 * with `only: ['state', 'filters', 'kpis', 'trend', 'top_missions']`.
 * Shell + sidebar stay mounted.
 */
export default function BrandAnalyticsMissionsIndex({
  state,
  filters,
  kpis,
  trend,
  top_missions: topMissions,
}: MissionAnalyticsOverviewProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("analytics.head_title")} />
      <div
        className="flex flex-col gap-6 p-4 md:p-6"
        data-testid="analytics-overview-page"
      >
        {/* OKS-65 — Shared tab strip above the h1 (TRD K13). URL-driven
         * active state; Inertia <Link> siblings swap pages on click. */}
        <BrandAnalyticsTabs active="missions" />

        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("analytics.overview.heading")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("analytics.overview.subheading")}
          </p>
        </header>

        {state === "error" ? (
          <AnalyticsErrorState
            reloadOnly={["state", "filters", "kpis", "trend", "top_missions"]}
          />
        ) : state === "cold_start" ? (
          <AnalyticsOverviewEmpty />
        ) : (
          <>
            <AnalyticsFilterBar filters={filters} />
            {kpis ? <AnalyticsOverviewKpiStrip kpis={kpis} /> : null}
            {trend ? <AnalyticsTrendChart trend={trend} /> : null}
            {topMissions ? (
              <AnalyticsTopMissionsTable topMissions={topMissions} />
            ) : null}
          </>
        )}
      </div>
    </AppLayout>
  )
}
